import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, PenTool, Share2, BarChart3, Calendar,
  FileText, Users, CreditCard, Settings, LogOut, Menu, X,
  UserSquare2, ShieldCheck, Package, UserCircle2, ImageIcon, Film, Palette,
  Link2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Logo, LangThemeToggle, NotificationsBell, UserMenu } from "@/components/layout/AppChrome";

const buildNav = (role) => {
  const base = [
    { key: "dashboard", to: "/app/dashboard", icon: LayoutDashboard, label: { en: "Dashboard", ar: "لوحة التحكم" } },
  ];
  const clientsForStaff = ["team", "admin", "super_admin"].includes(role) ? [
    { key: "clients", to: "/app/clients", icon: UserSquare2, label: { en: "Clients", ar: "العملاء" } },
  ] : [];
  const rest = [
    { key: "ai", to: "/app/assistant", icon: Sparkles, label: { en: "AI Assistant", ar: "المساعد الذكي" } },
    { key: "studio", to: "/app/studio", icon: PenTool, label: { en: "Content Studio", ar: "استوديو المحتوى" } },
    { key: "images", to: "/app/images", icon: ImageIcon, label: { en: "Images", ar: "الصور" } },
    { key: "videos", to: "/app/videos", icon: Film, label: { en: "Videos", ar: "الفيديو" } },
    { key: "brand",  to: "/app/brand",  icon: Palette, label: { en: "Brand", ar: "الهوية" } },
    { key: "social", to: "/app/social", icon: Share2, label: { en: "Social Accounts", ar: "الحسابات الاجتماعية" } },
    { key: "calendar", to: "/app/calendar", icon: Calendar, label: { en: "Content Calendar", ar: "تقويم المحتوى" } },
    { key: "analytics", to: "/app/analytics", icon: BarChart3, label: { en: "Analytics", ar: "التحليلات" } },
    { key: "reports", to: "/app/reports", icon: FileText, label: { en: "Reports", ar: "التقارير" } },
    { key: "team", to: "/app/team", icon: Users, label: { en: "Team", ar: "الفريق" } },
    { key: "plans", to: "/app/plans", icon: Package, label: { en: "Subscription Plans", ar: "خطط الاشتراك" } },
    { key: "billing", to: "/app/billing", icon: CreditCard, label: { en: "Billing", ar: "الفوترة" } },
  ];
  // DAAM Leads P0 — client-only feature. Placed after core content tools so
  // it sits next to social/analytics visually.
  const leadsForClient = role === "client" ? [
    { key: "leads", to: "/app/leads", icon: Link2, label: { en: "DAAM Leads", ar: "DAAM Leads" } },
  ] : [];
  const adminItems = ["admin", "super_admin"].includes(role) ? [
    { key: "admin", to: "/app/admin", icon: ShieldCheck, label: { en: "Admin Panel", ar: "لوحة الإدارة" } },
  ] : [];
  const tail = [
    { key: "profile", to: "/app/profile", icon: UserCircle2, label: { en: "Profile", ar: "ملفّي" } },
    { key: "settings", to: "/app/settings", icon: Settings, label: { en: "Settings", ar: "الإعدادات" } },
  ];
  return [...base, ...clientsForStaff, ...rest, ...leadsForClient, ...adminItems, ...tail];
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const NAV_ITEMS = buildNav(user?.role || "client");

  const Sidebar = (
    <aside data-testid="app-sidebar" className="w-60 shrink-0 border-e border-border/60 bg-card/50 backdrop-blur-xl flex flex-col h-screen sticky top-0">
      <div className="px-5 py-5 border-b border-border/40">
        <Logo />
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {NAV_ITEMS.map(({ key, to, icon: Icon, label }) => (
          <NavLink
            key={key}
            to={to}
            data-testid={`nav-${key}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all ${
                isActive
                  ? "bg-primary/10 text-primary border-s-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60 border-s-2 border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label[lang] || label.en}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border/40">
        <button
          data-testid="sidebar-logout-btn"
          onClick={async () => { await logout(); nav("/"); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-muted-foreground hover:text-destructive hover:bg-muted/60"
        >
          <LogOut className="w-4 h-4" />
          <span>{lang === "ar" ? "خروج" : "Log out"}</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 start-0 z-50 lg:hidden">{Sidebar}</div>
        </>
      )}

      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-border/40 bg-background/80 backdrop-blur-xl">
          <div className="flex items-center justify-between px-4 md:px-6 py-3 gap-3">
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 hover:bg-muted rounded-sm"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex-1" />
            <LangThemeToggle />
            <NotificationsBell />
            <UserMenu />
          </div>
        </header>
        <main className="max-w-[1500px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}
